import { ArrowGo, Heart, HeartFilled, Share } from "@assets"
import { Head, Link, router } from "@inertiajs/react"

import { AuthButton } from "@/components/auth-button"
import { ContentActionsMenu } from "@/components/content-actions-menu"
import { PageHeader } from "@/components/page-header"
import { Button } from "@/components/ui/button"
import { useFavorites } from "@/hooks/use-favorites"
import AppLayout from "@/layouts/app-layout"
import { interviewPath, interviewsEditPath, interviewsPath } from "@/routes"

interface Interview {
  id: number | string
  title: string
  interviewee_name?: string | null
  content: string // HTML
  cover_url?: string | null
  author?: { id: number; name: string; verified?: boolean }
  created_at?: string
  is_favorited?: boolean
  favorites_count?: number
}

interface Props {
  interview: Interview
  can_edit?: boolean
}

export default function InterviewShow({ interview, can_edit = false }: Props) {
  const {
    isFavorited: getStoreFavorite,
    setFavorite: setStoreFavorite,
    getFavoritesCount: getStoreCount,
    setFavoritesCount: setStoreCount,
  } = useFavorites()

  const interviewId = Number(interview.id)

  // Use store value if available, otherwise fallback to props
  const storedFavorite = getStoreFavorite("interview", interviewId)
  const storedCount = getStoreCount("interview", interviewId)
  const actualFavorited = storedFavorite ?? interview.is_favorited
  const actualFavoritesCount = storedCount ?? interview.favorites_count ?? 0

  const handleShare = async () => {
    try {
      const url = `${window.location.origin}${interviewPath(interview.id)}`
      const title = interview?.title ?? "Entrevista"
      if (navigator.share) {
        await navigator.share({ title, url })
      } else if (
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === "function"
      ) {
        await navigator.clipboard.writeText(url)
      }
    } catch {
      // no-op
    }
  }

  const handleEdit = () => router.visit(interviewsEditPath(interview.id))
  const handleDelete = () => {
    if (
      !confirm("¿Eliminar esta entrevista? Esta acción no se puede deshacer.")
    )
      return
    router.delete(interviewPath(interview.id), {
      preserveScroll: false,
      onSuccess: () => router.visit(interviewsPath()),
    })
  }
  const handleFavorite = () => {
    const newValue = !actualFavorited
    const newCount = newValue
      ? actualFavoritesCount + 1
      : Math.max(0, actualFavoritesCount - 1)

    // Sync with global store immediately for optimistic update
    setStoreFavorite("interview", interviewId, newValue)
    setStoreCount("interview", interviewId, newCount)

    router.post(
      `${interviewPath(interview.id)}/favorite`,
      {},
      {
        preserveScroll: true,
        onError: () => {
          // Revert on error
          setStoreFavorite("interview", interviewId, !newValue)
          setStoreCount(
            "interview",
            interviewId,
            newValue ? Math.max(0, newCount - 1) : newCount + 1,
          )
        },
      },
    )
  }

  const cleanedContent =
    interview.content?.replace(
      /<blockquote>[\s\S]*?<\/blockquote>/g,
      (blockquote) =>
        blockquote.replace(/<p>'/g, "<p>").replace(/'<\/p>/g, "</p>"),
    ) ?? ""

  return (
    <AppLayout
      showEditButton={can_edit}
      showDeleteButton={can_edit}
      showBackButton={false}
      showShareButton={false}
      showFavoriteButton={false}
      isFavorite={actualFavorited}
      onShare={() => {
        void handleShare()
      }}
      onEdit={handleEdit}
      onDelete={handleDelete}
      onFavorite={handleFavorite}
    >
      <Head title={`${interview.title} - Entrevistas`} />

      <div className="mx-auto max-w-6xl space-y-4 md:space-y-6 md:p-6">
        {/* Desktop Navigation */}
        <div className="hidden md:block">
          <PageHeader
            showBackButton
            onBack={() => router.visit(interviewsPath())}
            showEdit={can_edit}
            showDelete={can_edit}
            showShare={true}
            showFavorite={true}
            isFavorite={actualFavorited}
            editHref={interviewsEditPath(interview.id)}
            onShare={() => {
              void handleShare()
            }}
            onDelete={handleDelete}
            onFavorite={handleFavorite}
          />
        </div>
        <div className="px-4">
          <div className="text-primary flex flex-wrap items-center gap-2 text-[13px] font-medium tracking-[0.13px]">
            <Link href={interviewsPath()}>
              <div className="flex items-center gap-1 pt-[3px]">
                <ArrowGo className="text-primary size-4 rotate-180 fill-current" />
                Entrevistas
              </div>
            </Link>
          </div>
        </div>

        <div className="">
          {/* Contenido principal */}
          <div className="flex items-start justify-between gap-2 px-[10px]">
            <h1 className="text-foreground text-[21px] leading-[28px] font-semibold tracking-[0px]">
              {interview.interviewee_name}: &apos;{interview.title}&apos;
            </h1>
            <ContentActionsMenu
              contentType="Interview"
              contentId={Number(interview.id)}
              authorId={interview.author?.id ?? 0}
              authorName={interview.author?.name ?? "Autor"}
            />
          </div>
          <div className="mb-2 flex flex-wrap items-center gap-[14px] p-[10px]">
            <div className="flex items-center gap-[4px]">
              <AuthButton
                variant="ghost"
                size="icon"
                className="h-[14px] w-[16px]"
                onClick={() => void handleFavorite()}
              >
                {actualFavorited ? (
                  <HeartFilled className="text-destructive size-4.5 w-auto fill-current" />
                ) : (
                  <Heart className="size-4.5 w-auto" />
                )}
                <span className="sr-only">
                  {actualFavorited
                    ? "Quitar de favoritos"
                    : "Agregar a favoritos"}
                </span>
              </AuthButton>
              <span className="text-muted-foreground text-[13px] tracking-[0.20px]">
                {actualFavoritesCount}
              </span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-[20px] w-[20px]"
              onClick={() => void handleShare()}
            >
              <Share className="size-5" />
              <span className="sr-only">Compartir</span>
            </Button>
          </div>
          {interview.cover_url && (
            <div className="relative overflow-hidden">
              <img
                src={interview.cover_url}
                alt={interview.title}
                className="h-full max-h-[231px] w-full object-cover md:max-h-[520px] lg:max-h-[520px]"
              />
            </div>
          )}

          {/* Cuerpo de la entrevista */}
          <div
            className="interview-content [&_iframe]:aspect-video [&_iframe]:w-full"
            dangerouslySetInnerHTML={{ __html: cleanedContent }}
          />
        </div>
      </div>
    </AppLayout>
  )
}
